import type {
  ScheduleUnit,
  Weekday,
} from "../components/organisms/DeliverStory/types";

/**
 * Shared schedule-form constants. Used by:
 *   - DeliverStory's ScheduleFields (delivery scheduling)
 *   - ScheduleRefreshModal (connector dataset refresh)
 *
 * Keep this the single source of truth — adding/renaming a unit, timezone,
 * or weekday label here updates every consumer at once.
 */

export const UNIT_OPTIONS: { value: ScheduleUnit; label: string }[] = [
  { value: "day", label: "Day" },
  { value: "week", label: "Week" },
  { value: "month", label: "Month" },
];

/** 24 hours, formatted "1 am" / "12 pm" etc. */
export const HOUR_OPTIONS: { value: string; label: string }[] = Array.from(
  { length: 24 },
  (_, i) => {
    const suffix = i < 12 ? "am" : "pm";
    const hour12 = i % 12 === 0 ? 12 : i % 12;
    return { value: String(i), label: `${hour12} ${suffix}` };
  },
);

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Los_Angeles",
  "Europe/London",
  "Asia/Kolkata",
  "Asia/Tokyo",
];

/**
 * IANA timezone identifiers supported by the browser's Intl implementation.
 * `UTC` is added explicitly because some implementations omit it from
 * `supportedValuesOf`, although it is a valid timezone everywhere.
 */
const getTimezones = (): string[] => {
  const supportedValuesOf = Intl.supportedValuesOf;
  const timezones =
    typeof supportedValuesOf === "function"
      ? supportedValuesOf("timeZone")
      : FALLBACK_TIMEZONES;

  // Keep the commonly selected aliases too. For example, some ICU datasets
  // canonicalise `Asia/Kolkata` to `Asia/Calcutta`, while the API contract
  // accepts the modern IANA identifier entered by the user.
  return Array.from(new Set([...FALLBACK_TIMEZONES, ...timezones]));
};

export const TIMEZONE_OPTIONS: { value: string; label: string }[] =
  getTimezones().map((timezone) => ({
    value: timezone,
    label: timezone,
  }));

/** A fixed January day: zone names then read as the standard-time name, whatever today's date. */
const ZONE_NAME_REFERENCE = new Date(Date.UTC(2025, 0, 15, 12));

/** "Pacific Standard Time" -> "Pacific"; "Greenwich Mean Time" -> "Greenwich Mean". */
const shortZoneName = (longName: string): string =>
  longName.replace(/ (Standard|Daylight|Summer) Time$/, "").replace(/ Time$/, "");

/**
 * A timezone as "Pacific · Los Angeles": the zone's long name from Intl (without "Standard Time")
 * and the city from the IANA id. `UTC` and ids with no city read as just the name.
 */
export const formatTimezoneLabel = (timeZone: string): string => {
  if (timeZone === "UTC") return "UTC";
  const segments = timeZone.split("/");
  const city = segments[segments.length - 1].replace(/_/g, " ");
  let name: string | undefined;
  try {
    name = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "long" })
      .formatToParts(ZONE_NAME_REFERENCE)
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    name = undefined;
  }
  const region = name ? shortZoneName(name) : "";
  if (!region || region === city) return city;
  return segments.length > 1 ? `${region} · ${city}` : region;
};

/** An hour of the day as "9:00 AM" through Intl (the browser's spacing is normalised to a plain space). */
export const formatHourLabel = (hour: number): string =>
  new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "UTC" })
    .format(new Date(Date.UTC(2000, 0, 1, hour)))
    .replace(/\s/g, " ");

/** 24 hours as "12:00 AM" … "11:00 PM". The distribution schedule's wording; `HOUR_OPTIONS` stays "1 am". */
export const TIME_OF_DAY_OPTIONS: { value: string; label: string }[] = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: formatHourLabel(i),
}));

/** The same zones as `TIMEZONE_OPTIONS`, labelled "Pacific · Los Angeles" (the IANA id stays the value). */
export const TIMEZONE_NAMED_OPTIONS: { value: string; label: string }[] = TIMEZONE_OPTIONS.map(({ value }) => ({
  value,
  label: formatTimezoneLabel(value),
}));

/** Single-letter labels for the weekday day-pills row. */
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  sun: "S",
  mon: "M",
  tue: "T",
  wed: "W",
  thu: "T",
  fri: "F",
  sat: "S",
};

/** Full weekday name — used for screen-reader aria-labels. */
export const WEEKDAY_FULL: Record<Weekday, string> = {
  sun: "Sunday",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
};
